/**
 * Workforce reports suite (feature backlog #18). Every report returns the same shape - summary
 * figures, an optional chart and one or more tables with typed columns - so the web renders all of
 * them with one layout and the API exports any table to CSV/XLSX without per-report code.
 */
import type { ISODate, UUID } from '../types';

export type ReportKey = 'scheduled' | 'time_off' | 'availability' | 'skills' | 'worked' | 'variance' | 'attendance' | 'audit';

export interface ReportInfo { key: ReportKey; title: string; description: string; /** lucide icon name */ icon: string; permission: string }

export const REPORTS: ReportInfo[] = [
  { key: 'scheduled', title: 'Scheduled hours', description: 'Hours and shifts per employee by site, team or position, plus open shifts.', icon: 'CalendarClock', permission: 'workforce.reports.view' },
  { key: 'worked', title: 'Worked hours', description: 'Approved hours and cost by schedule or employee: base, premium, overtime and breaks.', icon: 'Timer', permission: 'workforce.reports.view' },
  { key: 'variance', title: 'Scheduled vs actual', description: 'Who worked more or less than scheduled, the cost impact, and unscheduled shifts.', icon: 'GitCompareArrows', permission: 'workforce.reports.view' },
  { key: 'attendance', title: 'Attendance', description: 'Attended shifts, no-shows and late clock-ins per employee, with a per-shift view.', icon: 'UserCheck', permission: 'workforce.reports.view' },
  { key: 'time_off', title: 'Time off', description: 'Leave taken per employee and leave type, with entitlement remaining.', icon: 'Palmtree', permission: 'workforce.reports.view' },
  { key: 'availability', title: 'Employee availability', description: 'Who can work a given window - availability, shifts, leave, WTR and skills - and offer the shift.', icon: 'CalendarSearch', permission: 'workforce.reports.view' },
  { key: 'skills', title: 'Skill expiry', description: 'Expired certificates and those expiring soon, by employee and skill.', icon: 'BadgeCheck', permission: 'workforce.reports.view' },
  { key: 'audit', title: 'Schedule audit log', description: 'Who changed rotas, timesheets, leave and schedule settings, with before and after values.', icon: 'History', permission: 'workforce.audit.view' },
];

export type ColumnKind = 'text' | 'date' | 'datetime' | 'time' | 'minutes' | 'signed_minutes' | 'pence' | 'signed_pence' | 'percent' | 'number' | 'days' | 'badge' | 'diff' | 'person';

export interface ReportColumn {
  key: string;
  header: string;
  kind: ColumnKind;
  /** Pay/cost column: dropped unless the viewer holds workforce.pay.view. */
  pay?: boolean;
  /** Hidden in the on-screen table (still exported). */
  exportOnly?: boolean;
}

export type ReportRow = Record<string, unknown> & {
  /** Drill-down: filter overrides that open a more detailed view of this row. */
  _drill?: { view?: string; label: string; filters: Partial<ReportFilters> };
};

export interface ReportTable { key: string; title: string; columns: ReportColumn[]; rows: ReportRow[]; totals?: Record<string, unknown>; empty?: string }

export interface ReportStat { label: string; value: number | string | null; kind: ColumnKind; sub?: string; pay?: boolean }

export interface ReportChart {
  title: string;
  kind: ColumnKind;
  /** Stacked horizontal bars: one per label, each with one or more segments. */
  bars: { label: string; segments: { key: string; label: string; value: number }[] }[];
}

export interface ReportFilters {
  from?: ISODate;
  to?: ISODate;
  location_ids?: UUID[];
  team_ids?: UUID[];
  position_ids?: UUID[];
  user_ids?: UUID[];
  group_by?: 'location' | 'team' | 'position' | 'employee';
  view?: string;
  include_unapproved?: boolean;
  leave_type_ids?: UUID[];
  skill_ids?: UUID[];
  days?: number;
  category?: 'all' | 'schedule' | 'timesheet' | 'leave' | 'people' | 'settings';
  actor_ids?: UUID[];
  date?: ISODate;
  start_time?: string;
  end_time?: string;
}

export interface ReportResponse {
  report: ReportKey;
  view: string;
  filters: ReportFilters;
  /** Whether cost figures are included: for every site in the report, only some, or none. */
  pay: 'all' | 'partial' | 'none';
  summary: ReportStat[];
  chart?: ReportChart;
  tables: ReportTable[];
  notes: string[];
  /** Extra structured data some views need (e.g. the availability window for quick offers). */
  meta?: Record<string, unknown>;
}

export interface ReportPreset { id: UUID; report: ReportKey; name: string; filters: ReportFilters; shared: boolean; user_id: UUID; mine: boolean }

/** Attendance outcome of one scheduled shift (computed in SQL; mirrored here for the UI and docs). */
export type AttendanceStatus = 'on_time' | 'late' | 'no_show' | 'excused' | 'pending' | 'in_progress';

export const ATTENDANCE_LABEL: Record<AttendanceStatus, string> = {
  on_time: 'On time', late: 'Late', no_show: 'No-show', excused: 'On leave', pending: 'Not due yet', in_progress: 'In progress',
};

/** Skill validity on a date. */
export type SkillStatus = 'valid' | 'expiring' | 'expired' | 'missing' | 'no_expiry';

export function skillStatus(expiresOn: string | null | undefined, today: string, warnDays: number): SkillStatus {
  if (!expiresOn) return 'no_expiry';
  if (expiresOn < today) return 'expired';
  const diff = Math.round((Date.parse(`${expiresOn}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86400000);
  return diff <= warnDays ? 'expiring' : 'valid';
}

/** A skill is usable for a shift on `date` when it has no expiry or expires on/after that date. */
export function skillValidOn(expiresOn: string | null | undefined, date: string): boolean {
  return !expiresOn || expiresOn >= date;
}

/** Comma-separated list params (`a,b,c`) as used in report URLs. */
export function listParam(v: unknown): string[] | undefined {
  if (v == null || v === '') return undefined;
  const arr = Array.isArray(v) ? v.flatMap(x => String(x).split(',')) : String(v).split(',');
  const out = arr.map(s => s.trim()).filter(Boolean);
  return out.length ? out : undefined;
}

/** Serialises filters to query params (arrays comma-joined, empty values dropped). */
export function filtersToQuery(f: ReportFilters): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(f)) {
    if (v == null || v === '' || (Array.isArray(v) && !v.length)) continue;
    out[k] = Array.isArray(v) ? v.join(',') : String(v);
  }
  return out;
}

export interface ReportCatalogue {
  reports: ReportInfo[];
  pay: boolean;
  can_offer: boolean;
  can_manage_skills: boolean;
  settings: { clock_in_early_minutes: number; late_grace_minutes: number; no_show_after_minutes: number; overtime_weekly_hours: number; overtime_enabled?: boolean; [k: string]: unknown };
}
